import {
  AcademicCalendar,
  CourseCalendar,
  CoursePlan,
  GenericReferenceId,
  NonInstructionDate,
  Weekday,
} from "@examspark/types";

import { getUnitDurationFromCourseUnitTag } from "../unit";

import { exists } from ".";

// evenly distribute days across durations with minimum error
function largestRemainderMethod(
  totalDates: number,
  durations: number[],
): number[] {
  // Calculate the total number of votes
  const totalDurations = durations.reduce(sum, 0);

  // Calculate the initial quotas for each candidate
  const quotas = durations.map((candidateVotes) =>
    Math.floor((candidateVotes / totalDurations) * totalDates),
  );

  // Calculate the remaining seats
  let remainingDates = totalDates - quotas.reduce(sum, 0);

  // Allocate the remaining seats to candidates with the largest remainders
  while (--remainingDates >= 0) {
    let maxRemainderIndex = -1;
    let maxRemainder = -1;

    for (let i = 0; i < durations.length; i++) {
      const remainder =
        (durations[i] / totalDurations) * totalDates - quotas[i];

      if (remainder > maxRemainder) {
        maxRemainderIndex = i;
        maxRemainder = remainder;
      }
    }

    quotas[maxRemainderIndex]++;
  }

  return quotas;
}

// Evenly spread grading periods across the academic calendar
export function getDefaultGradingPeriods(
  calendar: Pick<
    AcademicCalendar,
    | "startAt"
    | "endAt"
    | "gradingPeriodLength"
    | "instructionWeekdays"
    | "nonInstructionDates"
  >,
): AcademicCalendar["gradingPeriods"] {
  const allDaysMutable = instructionDatesBetween(
    calendar,
    calendar.startAt,
    calendar.endAt,
  );
  const LRMDates = largestRemainderMethod(
    allDaysMutable.length,
    Array(calendar.gradingPeriodLength === "6_WEEKS" ? 6 : 4).fill(1),
  ).map((val) => allDaysMutable.splice(0, val));

  return LRMDates.map((duration, idx) => {
    // this is mostly to convice the type system
    const startAt = duration.at(0);
    const endAt = duration.at(-1);
    if (!startAt || !endAt) {
      return null;
    }

    return {
      label: `Grading Period ${idx + 1}`,
      startAt,
      endAt,
    };
  }).filter(exists);
}

// After _much_ deliberation with TCMPC, they have decided that they do not
// like it when the units butt up against each other, but they _do_ like that
// the units are evenly distributed. This function will truncate the units to
// the original length on the course.
function truncatePlansToOriginalLength(units: string[][], durations: number[]) {
  return units.map((dates, idx) => dates.slice(0, durations[idx]));
}

// get an array of dates between two dates (inclusive)
export function* dateRange(
  startDate: Date,
  endDate: Date,
  stepMs = 1000 * 60 * 60 * 24,
) {
  let date = startDate;
  while (date <= endDate && date >= startDate) {
    yield date;
    date = new Date(stepMs + date.getTime());
  }
}

export const instructionDatesBetween = (
  calendar: Pick<
    AcademicCalendar,
    "nonInstructionDates" | "instructionWeekdays"
  >,
  startAt: string,
  endAt: string,
) => {
  const nonInstructionDates = new Set(
    calendar.nonInstructionDates?.flatMap((day) =>
      [
        fromAWSDate(day.startAt),
        ...dateRange(
          fromAWSDate(day.startAt),
          fromAWSDate(day.endAt ?? day.startAt),
        ),
      ].map((d) => toAWSDate(d)),
    ),
  );

  const weekdaySet = new Set(calendar.instructionWeekdays);
  const validDates = [...dateRange(fromAWSDate(startAt), fromAWSDate(endAt))]
    .filter((day) => weekdaySet.has(getDay(day)))
    .filter((day) => !nonInstructionDates.has(toAWSDate(day)));

  return validDates.map(toAWSDate);
};

export const weekdays = [
  Weekday.Sunday,
  Weekday.Monday,
  Weekday.Tuesday,
  Weekday.Wednesday,
  Weekday.Thursday,
  Weekday.Friday,
  Weekday.Saturday,
] as const;

export const locale = new Intl.DateTimeFormat("en-US", { timeZone: "UTC" });

/**
 * Converts string `2024-01-01Z` to new Date object
 *
 * @param date
 * @returns
 */
export const fromAWSDate = (date: string) => {
  const regularDate = date.substring(0, 10);
  const AWSExtendedOffset = date.substring(10);

  return new Date(`${regularDate}T00:00:00${AWSExtendedOffset}`);
};

/**
 * Converts Date to timezone-agnostic UTC string ending with Z.
 * note: this will break in 8,000 years
 */
export const toAWSDate = (date: Date) => {
  const regularDate = date.toISOString().substring(0, 10);
  const AWSExtendedOffset = "Z";

  return `${regularDate}${AWSExtendedOffset}`;
};

// "Jul 5"
export const shortLocale = Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  timeZone: "UTC",
});

export const formatShortDate = (date: Date) => shortLocale.format(date);

// "Wed, July 5, 2024"
export const longLocale = Intl.DateTimeFormat("en-US", {
  month: "long",
  weekday: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
});

export const formatLongDate = (date: Date) => longLocale.format(date);

export const getDay = (date: Date) =>
  date
    .toLocaleDateString("en-US", { weekday: "long", timeZone: "UTC" })
    .toUpperCase() as Weekday;

const sum = (acc: number, cur: number) => acc + cur;

export const coursePlanner = (params: {
  calendar: AcademicCalendar;
  unitIds: GenericReferenceId[] | null | undefined;
}): CoursePlan => {
  const validDates = instructionDatesBetween(
    params.calendar,
    params.calendar.startAt,
    params.calendar.endAt,
  );

  if (params.unitIds == null) {
    console.error(
      "Attempted to plan a course with null units. Did you forget to fetch them?",
    );
    return {
      units: [],
    };
  }

  const unitDurations = params.unitIds.map(
    (courseUnit) => +(getUnitDurationFromCourseUnitTag(courseUnit) ?? 0),
  );

  // determine how may days are in each unit (rounding to minimize error)
  // and then cut the front of the date array by that amount in order to get
  // the dates for each unit
  const unitDatesByIndex = largestRemainderMethod(
    validDates.length,
    unitDurations,
  ).map((val) => validDates.splice(0, val));

  const unitDatesByIndexTruncated = truncatePlansToOriginalLength(
    unitDatesByIndex,
    unitDurations,
  );

  return {
    units: unitDatesByIndexTruncated.map((unitDates, unitIndex) => ({
      id: params.unitIds?.[unitIndex].id,
      dates: unitDates,
    })),
  };
};

export const nonInstructionDatesByDate = (
  nonInstructionDates: AcademicCalendar["nonInstructionDates"],
) => {
  return nonInstructionDates?.reduce(
    (acc, cur) => {
      for (const d of [
        fromAWSDate(cur.startAt),
        ...dateRange(
          fromAWSDate(cur.startAt),
          fromAWSDate(cur.endAt ?? cur.startAt),
        ),
      ]) {
        acc[toAWSDate(d)] = cur;
      }

      return acc;
    },
    {} as Record<string, NonInstructionDate>,
  );
};

export const previousInstructionalDate = (params: {
  calendar: AcademicCalendar;
  date: string;
}): string | undefined => {
  const validDates = instructionDatesBetween(
    params.calendar,
    params.calendar.startAt,
    params.calendar.endAt,
  );

  const dateIndex = validDates.findIndex((date) => date === params.date);

  return validDates[dateIndex - 1];
};

export const nextInstructionalDate = (params: {
  calendar: AcademicCalendar;
  date: string;
}): string | undefined => {
  const validDates = instructionDatesBetween(
    params.calendar,
    params.calendar.startAt,
    params.calendar.endAt,
  );

  const dateIndex = validDates.findIndex((date) => date === params.date);

  return validDates[dateIndex + 1];
};

// push additional non-instruction dates into the academic calendar
export const getFullAcademicCalendarFromCourseCalendar = (
  courseCalendar:
    | Pick<CourseCalendar, "calendar" | "additionalNonInstructionDates">
    | undefined,
) => {
  const academicCalendar: AcademicCalendar | undefined =
    courseCalendar?.calendar
      ? {
          ...courseCalendar.calendar,
          nonInstructionDates: [
            ...(courseCalendar.calendar.nonInstructionDates ?? []),
            ...(courseCalendar.additionalNonInstructionDates ?? []),
          ],
        }
      : undefined;

  return academicCalendar;
};

export const validateAcademicCalendar = (
  calendar: Omit<AcademicCalendar, "id" | "sk">,
) => {
  if (!calendar.name) {
    throw new Error("Calendar must have a name.");
  }

  if (!calendar.startAt) {
    throw new Error("Calendar must have a start date.");
  }

  if (!calendar.endAt) {
    throw new Error("Calendar must have an end date.");
  }

  if (!calendar.instructionWeekdays?.length) {
    throw new Error("Calendar must have instruction weekdays.");
  }

  if (!calendar.gradingPeriodLength) {
    throw new Error("Calendar must have a grading period length.");
  }

  if (!calendar.gradingPeriods?.length) {
    throw new Error("Calendar must have grading periods.");
  }

  for (const nid of calendar.nonInstructionDates ?? []) {
    if (!nid.label?.length) {
      throw new Error("Non-instruction date must have a label.");
    }
    if (!nid.startAt) {
      throw new Error("Non-instruction date must have a start date.");
    }
  }

  if (calendar.startAt > calendar.endAt) {
    throw new Error("Calendar start date must be before end date.");
  }

  const daysLong =
    (+fromAWSDate(calendar.endAt) - +fromAWSDate(calendar.startAt)) /
    1000 /
    60 /
    60 /
    24;
  if (daysLong > 730) {
    throw new Error(
      `Calendar cannot last longer than 730 days (actual: ${daysLong})`,
    );
  }

  let i = -1;
  for (const gradingPeriod of calendar.gradingPeriods) {
    i++;
    if (!gradingPeriod.label) {
      throw new Error("Grading period must have a label.");
    }

    if (!gradingPeriod.startAt) {
      throw new Error("Grading period must have a start date.");
    }

    if (!gradingPeriod.endAt) {
      throw new Error("Grading period must have an end date.");
    }

    if (gradingPeriod.startAt > gradingPeriod.endAt) {
      throw new Error("Grading period start date must be before end date.");
    }

    const prev = calendar.gradingPeriods[i - 1];
    if (prev && gradingPeriod.startAt < prev.endAt) {
      throw new Error("Grading periods cannot overlap.");
    }
  }
};
